import { useEffect, useState } from "react";
import type { FlashcardsBlock } from "../contract/types";
import { Icon } from "../components/Icon";
import { BLOCK_EVENTS } from "./useBlockAction";
import type { BlockViewProps } from "./types";

/**
 * Mazo de repaso con volteo.
 *
 * Se marca tarjeta a tarjeta ("la sé" / "repasar") y al terminar se emite
 * `flashcards.reviewed` con `{known, total}`: eso es lo que el agente usa para
 * cerrar y decir qué conviene reforzar. El volteo es una transición real de
 * 3D —no un cambio de texto— porque el gesto es la mitad de la mecánica.
 */
export function Flashcards({ block, emit, interactive }: BlockViewProps<FlashcardsBlock>) {
  const cards = block.cards ?? [];
  const total = cards.length;
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState<Set<number>>(new Set());
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    setIndex(0);
    setFlipped(false);
    setKnown(new Set());
    setReviewed(false);
  }, [block.id]);

  if (total === 0) {
    return <p className="atom__empty">El mazo no trae tarjetas.</p>;
  }

  const card = cards[index];
  const isKnown = known.has(index);
  const answeredCount = known.size;

  function move(delta: number) {
    setFlipped(false);
    setIndex((current) => Math.min(total - 1, Math.max(0, current + delta)));
  }

  function toggleKnown() {
    setKnown((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function finish() {
    setReviewed(true);
    if (interactive) {
      emit(BLOCK_EVENTS.flashcardsReviewed, { known: known.size, total });
    }
  }

  return (
    <div className="atom atom--flashcards flashcards">
      <div className="flashcards__bar">
        <span className="flashcards__counter u-nums">
          {index + 1} / {total}
        </span>
        <span className="flashcards__known">
          <Icon name="check-circle" size="0.875rem" />
          {answeredCount} de {total} firmes
        </span>
      </div>

      <button
        type="button"
        className={`flashcards__card ${flipped ? "flashcards__card--flipped" : ""}`}
        onClick={() => setFlipped((value) => !value)}
        aria-label={flipped ? "Ver el anverso" : "Ver el reverso"}
      >
        <span className="flashcards__face flashcards__face--front">
          <span className="flashcards__eyebrow u-label">Término</span>
          <span className="flashcards__front-text">{card?.front}</span>
          {card?.hint ? <span className="flashcards__hint">{card.hint}</span> : null}
          <span className="flashcards__flip-hint">
            <Icon name="refresh" size="0.75rem" />
            Toca para ver la definición
          </span>
        </span>
        <span className="flashcards__face flashcards__face--back">
          <span className="flashcards__eyebrow u-label">Definición</span>
          <span className="flashcards__back-text">{card?.back}</span>
          <span className="flashcards__flip-hint">
            <Icon name="refresh" size="0.75rem" />
            Toca para volver
          </span>
        </span>
      </button>

      <div className="flashcards__nav">
        <button
          type="button"
          className="u-button"
          disabled={index === 0}
          onClick={() => move(-1)}
        >
          <Icon name="chevron-left" size="0.875rem" />
          Anterior
        </button>
        <button
          type="button"
          className={`u-button ${isKnown ? "u-button--primary" : ""}`}
          aria-pressed={isKnown}
          onClick={toggleKnown}
        >
          <Icon name={isKnown ? "check-circle" : "check"} size="0.875rem" />
          {isKnown ? "Firme" : "Marcar como firme"}
        </button>
        <button
          type="button"
          className="u-button"
          disabled={index >= total - 1}
          onClick={() => move(1)}
        >
          Siguiente
          <Icon name="chevron-right" size="0.875rem" />
        </button>
      </div>

      <div className="flashcards__progress" aria-hidden="true">
        {cards.map((_, cardIndex) => (
          <span
            key={cardIndex}
            className={[
              "flashcards__tick",
              cardIndex === index ? "flashcards__tick--current" : "",
              known.has(cardIndex) ? "flashcards__tick--known" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        ))}
      </div>

      {!reviewed ? (
        <button type="button" className="u-button u-button--primary flashcards__finish" onClick={finish}>
          Terminar repaso
        </button>
      ) : (
        <p className="flashcards__done" role="status">
          <Icon name="check-circle" size="1rem" />
          Repaso enviado: {known.size} de {total} tarjetas firmes.
        </p>
      )}
    </div>
  );
}

export default Flashcards;
